
import type React from "react"

import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { mockSubscriptions } from "@/lib/mock-data"

interface EditSubscriptionDialogProps {
  subscriptionId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditSubscriptionDialog({ subscriptionId, open, onOpenChange }: EditSubscriptionDialogProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [subscription, setSubscription] = useState<any>(null)

  useEffect(() => {
    if (open && subscriptionId) {
      // Find subscription by ID
      const foundSubscription = mockSubscriptions.find((s) => s.id === subscriptionId)
      setSubscription(foundSubscription || null)
    }
  }, [open, subscriptionId])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    // Simulate API call
    setTimeout(() => {
      setIsLoading(false)
      onOpenChange(false)
      toast({
        title: t("common.success"),
        description: t("subscriptions.planUpdated"),
      })
    }, 1000)
  }

  if (!subscription) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("subscriptions.editPlan")}</DialogTitle>
          <DialogDescription>{t("subscriptions.editPlanDescription")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="name" className="text-right">
                {t("subscriptions.name")}
              </Label>
              <Input id="name" defaultValue={subscription.name} className="col-span-3" required />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="price" className="text-right">
                {t("subscriptions.price")}
              </Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                defaultValue={subscription.price}
                className="col-span-3"
                required
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="duration" className="text-right">
                {t("subscriptions.duration")}
              </Label>
              <div className="col-span-3 flex gap-2">
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  defaultValue={subscription.duration}
                  className="flex-1"
                  required
                />
                <Select defaultValue={subscription.durationType}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">{t("subscriptions.days")}</SelectItem>
                    <SelectItem value="months">{t("subscriptions.months")}</SelectItem>
                    <SelectItem value="years">{t("subscriptions.years")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="description" className="text-right">
                {t("subscriptions.description")}
              </Label>
              <Textarea id="description" defaultValue={subscription.description} className="col-span-3" rows={3} />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="features" className="text-right">
                {t("subscriptions.features")}
              </Label>
              <Textarea id="features" defaultValue={subscription.features.join("\n")} className="col-span-3" rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? t("common.loading") : t("common.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
